import React, { useState } from 'react';
import { useField, ErrorMessage } from 'formik';
import styled from '@emotion/styled';
import { G300, N60, N500, N900, R400 } from '@atlaskit/theme/colors';
import Spinner from '@atlaskit/spinner';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { DebouncedFunc } from 'lodash';

import CheckCircleOutlineIcon from '@atlaskit/icon/glyph/check-circle-outline';

type TextInputProps = {
  readonly name: string;
  readonly label: React.ReactNode;
  readonly type: string;
  readonly testId?: string;
  readonly disabled?: boolean;
  readonly value?: string;
  readonly validateFn?: DebouncedFunc<
    (value: string) => Promise<string | undefined>
  >;
  readonly autoComplete?: string;
};

export const TextInput = ({
  name,
  label,
  testId,
  validateFn,
  ...props
}: TextInputProps) => {
  const [isValidating, setIsValidating] = useState(false);
  const [field, meta] = useField({
    name,
    ...(validateFn && {
      validate: async (value) => {
        setIsValidating(true);
        const error = await validateFn(value);
        setIsValidating(false);
        return error;
      }
    })
  });
  const InputFeedback = () => {
    if (isValidating) {
      return <Spinner size="small" />;
    }

    if (!!meta.error && !!meta.touched) {
      return <WarningIcon label="success" primaryColor={R400} />;
    }

    if (!meta.error && !!meta.touched) {
      return <CheckCircleOutlineIcon label="success" primaryColor={G300} />;
    }

    return null;
  };

  return (
    <FormField>
      <FormFieldLabel htmlFor={name}>{label}</FormFieldLabel>
      <FormFieldInputContainer
        showErrorState={!!meta.error && !!meta.touched}
        showValidState={!meta.error && !!meta.touched}
      >
        <FormFieldInput {...field} {...props} data-testid={testId} />
        {validateFn && (
          <EmailAfter>
            <InputFeedback />
          </EmailAfter>
        )}
      </FormFieldInputContainer>
      <ErrorMessage name={name} component={FormFieldErrorMessage} />
    </FormField>
  );
};

const EmailAfter = styled.div`
  padding-right: 8px;
  display: flex;
  font-size: 16px;
  align-items: center;
  svg {
    margin-bottom: 0px;
  }
`;

const fontCharlieText =
  '"Charlie Text", -apple-system, BlinkMacSystemFont, "Segoe UI",' +
  'Roboto, "Noto Sans", Ubuntu, "Droid Sans", "Helvetica Neue", sans-serif !important';

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 16px;
`;

export const FormFieldLabel = styled.label`
  font-family: ${fontCharlieText};
  font-weight: 500;
  font-size: 14px;
  color: ${N500};
  margin-bottom: 5px;
`;

const FormFieldInput = styled.input`
  width: 100%;
  outline: none;
  font-family: ${fontCharlieText};
  font-weight: 400;
  font-size: 14px;
  color: ${N900};
  padding: 8px 10px;
  height: 20px;
  border-style: none;
`;

const FormFieldInputContainer = styled.div`
  display: flex;
  justify-content: space-between;
  border-style: solid;
  border-radius: 3px;
  border-color: ${({
    showErrorState,
    showValidState
  }: {
    showErrorState: boolean;
    showValidState: boolean;
  }) => {
    if (showErrorState) {
      return R400;
    } else if (showValidState) {
      return G300;
    }
    return N60;
  }};
`;

const FormFieldErrorMessage = styled.span`
  color: ${R400};
  font-weight: 400;
  font-size: 12px;
  font-family: ${fontCharlieText};
  margin: 6px 0;
`;
